import {
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Divider from '@mui/material/Divider';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';

import { fileTypes } from '../../constants';
import PageHeader from '../reusable/PageHeader';
import UploadCard from '../reusable/UploadCard';
import { useState, useRef } from 'react';
import FocusGroupUploadPrompt from './FocusGroupUploadPrompt';
import { SummaryFailedAlert, SummaryUpdatingAlert } from './SummaryStatusAlerts';
import type { SchoolFocusGroup } from '../../services/focusGroupService';
import {
  FocusGroupFileStatus,
  formatDate,
  useAllSchoolFocusGroups,
  useSchoolFocusGroupReport,
} from '../../services/focusGroupService';
import type { UploadedFileData } from '../../interfaces/UploadedFileDataInterface';
import UploadResultsContext from '../../context/UploadResultsContext';
import Loader from '../reusable/Loader';
import AudioFileIcon from '../assets/file-type-audio.svg';
import { postRequestUploadUrl, uploadFileToS3Bucket } from '../../services/uploadService';
import useUserDetails from '../hooks/useUserDetails';

const FIVE_GB = 5 * 1024 * 1024 * 1024;

const formatBytes = (bytes: number) => {
  const units = ['B', 'KB', 'MB', 'GB'];
  let index = 0;

  while (bytes >= 1024) {
    bytes = bytes / 1024;
    index++;
  }

  return `${bytes.toFixed(0)} ${units[index]}`;
};

const SchoolReportStatus = ({ schoolId }: { schoolId: string }) => {
  const { schoolFocusGroupReport, loading } = useSchoolFocusGroupReport({ schoolId });

  if (loading || !schoolFocusGroupReport) return null;

  const { status } = schoolFocusGroupReport;

  if (status === 'GENERATION_FAILED') return <SummaryFailedAlert />;
  if (status === 'PROCESSING') return <SummaryUpdatingAlert />;

  return null;
};

const FocusGroupsOverview = () => {
  const [sort, setSort] = useState('recently-uploaded');
  const { schoolFocusGroups, loading: loadingSchoolFocusGroups } = useAllSchoolFocusGroups();
  const [uploadedFileData, setUploadedFileData] = useState<UploadedFileData[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentFileId, setCurrentFileId] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);

  const {
    userDetails: { data: userDetails },
  } = useUserDetails();

  const handleReplaceFile = (fileId: string) => {
    setCurrentFileId(fileId);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && userDetails) {
      setIsUploading(true);
      try {
        const urlResponse = await postRequestUploadUrl(userDetails.eoaId, userDetails.access_token, {
          file_name: file.name,
          user_provided_file_metadata: {
            school_id:
              schoolFocusGroups.find((school) =>
                school.focus_groups.some((fg) => fg.files.some((f) => f.data_file_id === currentFileId))
              )?.id || '',
            focus_group_datetime: new Date().toISOString(),
          },
          upload_file_type: 'FOCUS_GROUP_RECORDING',
          replaces_file_id: currentFileId,
        });

        await uploadFileToS3Bucket(urlResponse.data.presigned_url, file, (progress) => {
          console.log('Upload progress:', progress);
        });

        console.log('File replaced successfully:', file.name);
      } catch (error) {
        console.error('Error replacing file:', error);
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  if (loadingSchoolFocusGroups) {
    return <Loader />;
  }

  const sortedSchools = schoolFocusGroups.sort((a, b) => {
    if (sort === 'recently-uploaded') {
      const getMostRecentFocusGroup = (school: SchoolFocusGroup) => {
        const focusGroups = school.focus_groups;
        const mostRecentFocusGroup = focusGroups.sort((a, b) =>
          b.focus_group_datetime.localeCompare(a.focus_group_datetime)
        )[0];
        return mostRecentFocusGroup;
      };

      const mostRecentA = getMostRecentFocusGroup(a);
      const mostRecentB = getMostRecentFocusGroup(b);

      // return schools with no focus groups last
      if (!mostRecentA) return 1;
      if (!mostRecentB) return -1;

      return mostRecentA.files[0].upload_datetime.localeCompare(mostRecentB.files[0].upload_datetime);
    }

    return a.name.localeCompare(b.name);
  });

  return (
    <>
      <PageHeader
        title="Focus group report"
        pageDescription="Automatically generate transcripts and key points to create focus group reports"
      />
      <div className="main-content">
        <div className="overview-step-cards">
          <Card style={{ width: '100%', backgroundColor: 'transparent', boxShadow: 'none' }}>
            <div>
              <UploadResultsContext.Provider value={{ uploadResults: {}, uploadData: {}, dispatch: () => {} }}>
                <UploadCard
                  title="Upload focus group files"
                  maxSize={FIVE_GB}
                  helperText={['Please upload focus group recordings. Multiple files can be uploaded per school.']}
                  uploadedFilesData={uploadedFileData}
                  fileType={fileTypes.FOCUS_GROUP_RECORDING}
                  cardId="focus-group-transcripts"
                  uploadPrompt={
                    <FocusGroupUploadPrompt
                      uploadedFileData={uploadedFileData}
                      setUploadedFileData={setUploadedFileData}
                    />
                  }
                  acceptFileTypes={{
                    // Recordings
                    'audio/mpeg': ['.mp3'],
                    'video/mp4': ['.mp4'],
                    'audio/wav': ['.wav'],
                    'audio/aac': ['.aac'],
                    // Notes
                    'application/pdf': ['.pdf'],
                    // 'application/msword': ['.doc'],
                    // 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                    'text/plain': ['.txt'],
                    'application/rtf': ['.rtf'],
                    'text/html': ['.html'],
                  }}
                  showUploadProgress={false}
                  showAccordion={false}
                />
              </UploadResultsContext.Provider>
            </div>
          </Card>
          <Card style={{ width: '100%', borderRadius: '20px' }}>
            <CardHeader
              title="My focus groups"
              action={
                <FormControl fullWidth variant="standard">
                  <InputLabel id="sort-label">Sort</InputLabel>
                  <Select
                    labelId="sort-label"
                    value={sort}
                    displayEmpty
                    label="Sort"
                    onChange={(e) => setSort(e.target.value)}
                    size="small"
                    sx={{ width: '200px' }}
                  >
                    <MenuItem value="recently-uploaded">Recently Uploaded</MenuItem>
                    <MenuItem value="alphabetical">Alphabetical</MenuItem>
                  </Select>
                </FormControl>
              }
              sx={{ padding: '24px' }}
            />
            <CardContent>
              <List component="nav">
                {sortedSchools
                  .filter((school) => school.focus_groups.length > 0)
                  .map((school) => (
                    <div key={school.id}>
                      <ListItem
                        secondaryAction={
                          <Button
                            href={`/school-focus-group/${school.id}`}
                            endIcon={<ArrowForwardIosOutlinedIcon />}
                            variant="outlined"
                            size="small"
                          >
                            View
                          </Button>
                        }
                      >
                        <ListItemText primary={school.name} primaryTypographyProps={{ fontSize: '20px' }} />
                      </ListItem>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '16px',
                          width: '100%',
                          marginLeft: '16px',
                          marginRight: '16px',
                          paddingRight: '32px',
                        }}
                      >
                        <SchoolReportStatus schoolId={school.id} />
                      </div>
                      <List component="div">
                        {school.focus_groups.map((focusGroup, i) => {
                          const { files } = focusGroup;
                          const [file] = files;

                          const fileStatus = file.file_status;

                          return (
                            <div key={file.data_file_id} style={{ margin: '0px 0px 20px 40px' }}>
                              <div>
                                <b>
                                  {focusGroup.name} - {formatDate(focusGroup.focus_group_datetime)}
                                </b>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '12px',
                                  padding: '12px 0',
                                  borderBottom: '1px solid #f0f0f0',
                                }}
                              >
                                <div
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginTop: '2px',
                                  }}
                                >
                                  {fileStatus === FocusGroupFileStatus.TRANSCRIPTION_FAILED ||
                                  fileStatus === FocusGroupFileStatus.UPLOAD_FAILED ||
                                  fileStatus === FocusGroupFileStatus.VALIDATION_FAILED ||
                                  fileStatus === FocusGroupFileStatus.EXTRACTION_FAILED ||
                                  fileStatus === FocusGroupFileStatus.CORRUPT ||
                                  fileStatus === FocusGroupFileStatus.VIRUS_SCAN_FAILED ? (
                                    <WarningIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                                  ) : file.file_type === fileTypes.FOCUS_GROUP_RECORDING ? (
                                    <img
                                      src={AudioFileIcon}
                                      alt="audio file"
                                      style={{ width: '20px', height: '20px' }}
                                    />
                                  ) : (
                                    <DescriptionOutlinedIcon sx={{ color: '#000000', fontSize: 20 }} />
                                  )}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      fontSize: '16px',
                                      fontWeight: '400',
                                      color: '#000000',
                                      marginBottom: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '12px',
                                    }}
                                  >
                                    <span>{file.original_filename}</span>
                                    {(fileStatus === FocusGroupFileStatus.TRANSCRIPTION_FAILED ||
                                      fileStatus === FocusGroupFileStatus.UPLOAD_FAILED ||
                                      fileStatus === FocusGroupFileStatus.VALIDATION_FAILED ||
                                      fileStatus === FocusGroupFileStatus.EXTRACTION_FAILED ||
                                      fileStatus === FocusGroupFileStatus.CORRUPT ||
                                      fileStatus === FocusGroupFileStatus.VIRUS_SCAN_FAILED) && (
                                      <button
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          padding: '4px',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '50%',
                                          width: '32px',
                                          height: '32px',
                                          flexShrink: 0,
                                        }}
                                        onClick={() => handleReplaceFile(file.data_file_id)}
                                        disabled={isUploading}
                                        title="Retry upload"
                                      >
                                        <RefreshIcon sx={{ color: '#000000', fontSize: 20 }} />
                                      </button>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '14px',
                                      color: '#666666',
                                      marginBottom: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                    }}
                                  >
                                    <button
                                      style={{
                                        color: '#006666',
                                        textDecoration: 'underline',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        cursor: isUploading ? 'not-allowed' : 'pointer',
                                        opacity: isUploading ? 0.6 : 1,
                                      }}
                                      onClick={() => handleReplaceFile(file.data_file_id)}
                                      disabled={isUploading}
                                    >
                                      {isUploading ? 'Replacing...' : 'Replace file'}
                                    </button>
                                    <span>
                                      {formatBytes(file.file_size_bytes)} • Uploaded {formatDate(file.upload_datetime)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </List>
                      <Divider />
                    </div>
                  ))}
              </List>
            </CardContent>
          </Card>
        </div>
      </div>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".wav,.mp3,.m4a,.aac,.flac"
        onChange={handleFileChange}
      />
    </>
  );
};

export default FocusGroupsOverview;
